import { useEffect, useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Title, Text, Grid, Card, TabGroup, TabList, Tab } from '@tremor/react';
import { useQuery } from '@tanstack/react-query';
import { Download, Search } from 'lucide-react';

import { KPICard } from '@/components/KPICard';
import { StatusBadge } from '@/components/StatusBadge';
import { Sparkline, type SparklinePoint } from '@/components/Sparkline';
import { CohortMatrix, type CohortBucket } from '@/components/CohortMatrix';
import { LoadingSkeleton } from '@/components/LoadingSkeleton';
import { api, buildQueryString } from '@/lib/api';
import {
  formatRangeShort,
  useDateRange,
  useDateRangeKey,
} from '@/lib/dateRange';

interface UserListItem {
  id: number;
  name: string | null;
  email: string;
  role: string | null;
  org_id: number | null;
  org_name: string | null;
  signup_at: string | null;
  last_active_at: string | null;
  status: string;
  uploads_count: number;
  calc_completed_count: number;
  downloaded_count: number;
  total_paid_brl: number;
  last_device: string | null;
}

interface UserListResponse {
  items: UserListItem[];
  total: number;
  page: number;
  per_page: number;
  facets: Record<string, Record<string, number>>;
}

type CohortMetric = 'signups' | 'uploaders' | 'payers';

function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('pt-BR');
  } catch {
    return iso;
  }
}

function exportRowsToCsv(items: UserListItem[]): void {
  const headers = [
    'id',
    'email',
    'name',
    'role',
    'org_id',
    'org_name',
    'status',
    'signup_at',
    'last_active_at',
    'uploads_count',
    'calc_completed_count',
    'downloaded_count',
    'total_paid_brl',
    'last_device',
  ];
  const escape = (v: unknown): string => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const lines = [headers.join(',')];
  for (const it of items) {
    lines.push(headers.map((h) => escape((it as unknown as Record<string, unknown>)[h])).join(','));
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `jusquant-users-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function UsersPage() {
  const { range, apiParams } = useDateRange();
  const rangeKey = useDateRangeKey();
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounced(searchInput, 300);
  const [role, setRole] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [hasUploaded, setHasUploaded] = useState<boolean | undefined>(undefined);
  const [hasPaid, setHasPaid] = useState<boolean | undefined>(undefined);
  // When true the global date range is applied as a signup-window filter.
  const [filterBySignup, setFilterBySignup] = useState(false);
  const [page, setPage] = useState(1);
  const perPage = 50;
  const [cohortMetric, setCohortMetric] = useState<CohortMetric>('signups');

  const usersQuery = useQuery<UserListResponse>({
    queryKey: [
      'users.list',
      { search, role, hasUploaded, hasPaid, page, filterBySignup, rangeKey },
    ],
    queryFn: async () => {
      const qs = buildQueryString({
        page,
        per_page: perPage,
        q: search || undefined,
        role: role || undefined,
        has_uploaded: hasUploaded,
        has_paid: hasPaid,
        ...(filterBySignup ? apiParams : {}),
      });
      const r = await api().get(`/users${qs}`);
      return r.data as UserListResponse;
    },
  });

  const cohortsQuery = useQuery({
    queryKey: ['users.cohorts', cohortMetric],
    queryFn: async () => {
      const r = await api().get(
        `/users/cohorts?metric=${cohortMetric}&weeks=12`,
      );
      return r.data as { metric: string; weeks: number; cohorts: CohortBucket[] };
    },
  });

  // Top KPIs (derived client-side from the current page until we have the
  // dedicated /users/kpis endpoint in Phase 2).
  const items = usersQuery.data?.items ?? [];
  const facets = usersQuery.data?.facets ?? {};

  const dau = useMemo(() => items.filter((i) => i.status === 'active').length, [items]);
  const wau = useMemo(
    () => items.filter((i) => i.status === 'active' || i.status === 'dormant').length,
    [items],
  );

  const sparkData: SparklinePoint[] = useMemo(() => {
    // Fold last_active_at occurrences into a 30-day series.
    const byDay: Record<string, number> = {};
    for (let i = 29; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      byDay[key] = 0;
    }
    for (const it of items) {
      if (!it.last_active_at) continue;
      const key = it.last_active_at.slice(0, 10);
      if (key in byDay) byDay[key] += 1;
    }
    return Object.entries(byDay).map(([date, value]) => ({ date, value }));
  }, [items]);

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-3">
        <div>
          <Title>Users</Title>
          <Text>
            Lista, filtros e cohorts dos usuários da JusQuant. Cliques em uma
            linha abrem o detalhe completo.
          </Text>
        </div>
        <button
          type="button"
          onClick={() => exportRowsToCsv(items)}
          className="inline-flex items-center gap-1 rounded-md border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-800"
        >
          <Download className="h-3.5 w-3.5" /> CSV
        </button>
      </header>

      <Grid numItems={1} numItemsSm={2} numItemsLg={4} className="gap-4">
        <KPICard
          label="DAU (página atual)"
          value={String(dau)}
          hint="Ativos nos últimos 7 dias"
          loading={usersQuery.isLoading}
        />
        <KPICard
          label="WAU (página atual)"
          value={String(wau)}
          hint="Ativos + dormentes (≤30d)"
          loading={usersQuery.isLoading}
        />
        <KPICard
          label="Total"
          value={
            usersQuery.data ? String(usersQuery.data.total) : undefined
          }
          hint="Usuários no filtro atual"
          loading={usersQuery.isLoading}
        />
        <Card>
          <Text>Atividade (30d)</Text>
          <Sparkline data={sparkData} loading={usersQuery.isLoading} />
        </Card>
      </Grid>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <Card className="lg:col-span-8">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <input
                type="search"
                placeholder="Buscar por email ou nome…"
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  setPage(1);
                }}
                className="w-full rounded-md border border-slate-700 bg-slate-950 py-1.5 pl-8 pr-2 text-sm text-slate-100 focus:border-blue-500 focus:outline-none"
              />
            </div>

            <select
              value={role}
              onChange={(e) => {
                setRole(e.target.value);
                setPage(1);
              }}
              className="rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-slate-200"
            >
              <option value="">Todos os perfis</option>
              <option value="admin">admin</option>
              <option value="perito">perito</option>
              <option value="viewer">viewer</option>
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-slate-200"
            >
              <option value="">Todos os status</option>
              <option value="active">active</option>
              <option value="dormant">dormant</option>
              <option value="churned">churned</option>
              <option value="banned">banned</option>
            </select>

            <label
              className={`inline-flex cursor-pointer items-center gap-1.5 rounded-md border px-2 py-1.5 text-xs transition ${
                filterBySignup
                  ? 'border-blue-500/40 bg-blue-600/20 text-blue-200'
                  : 'border-slate-700 text-slate-300 hover:bg-slate-800'
              }`}
              title="Aplicar a janela global de datas como filtro de cadastro"
            >
              <input
                type="checkbox"
                checked={filterBySignup}
                onChange={(e) => {
                  setFilterBySignup(e.target.checked);
                  setPage(1);
                }}
                className="h-3 w-3 accent-blue-500"
              />
              Cadastro: {formatRangeShort(range)}
            </label>

            <button
              type="button"
              onClick={() => {
                setHasUploaded(hasUploaded === true ? undefined : true);
                setPage(1);
              }}
              className={`rounded-md border px-2 py-1 text-xs ${
                hasUploaded === true
                  ? 'border-blue-500 bg-blue-600/20 text-blue-200'
                  : 'border-slate-700 text-slate-300'
              }`}
            >
              já fez upload
            </button>
            <button
              type="button"
              onClick={() => {
                setHasPaid(hasPaid === true ? undefined : true);
                setPage(1);
              }}
              className={`rounded-md border px-2 py-1 text-xs ${
                hasPaid === true
                  ? 'border-blue-500 bg-blue-600/20 text-blue-200'
                  : 'border-slate-700 text-slate-300'
              }`}
            >
              já pagou
            </button>
          </div>

          {usersQuery.isLoading && <LoadingSkeleton rows={6} />}

          {usersQuery.isError && (
            <div className="rounded border border-red-900/40 bg-red-950/30 p-3 text-sm text-red-300">
              Falha ao carregar usuários.
            </div>
          )}

          {!usersQuery.isLoading && !usersQuery.isError && (
            <UsersTable
              items={items.filter(
                (i) => !statusFilter || i.status === statusFilter,
              )}
              page={usersQuery.data?.page ?? 1}
              total={usersQuery.data?.total ?? 0}
              perPage={usersQuery.data?.per_page ?? perPage}
              onPageChange={setPage}
            />
          )}

          {/* Facet chips (informational, current page only) */}
          {facets?.status && (
            <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-400">
              <span className="text-slate-500">Status nesta página:</span>
              {Object.entries(facets.status).map(([k, n]) => (
                <span
                  key={k}
                  className="rounded bg-slate-800 px-2 py-0.5 text-slate-300"
                >
                  {k}: {n}
                </span>
              ))}
            </div>
          )}
        </Card>

        <div className="lg:col-span-4">
          <div className="mb-3">
            <TabGroup
              index={['signups', 'uploaders', 'payers'].indexOf(cohortMetric)}
              onIndexChange={(i) =>
                setCohortMetric(
                  (['signups', 'uploaders', 'payers'][i] ?? 'signups') as CohortMetric,
                )
              }
            >
              <TabList variant="solid">
                <Tab>Signups</Tab>
                <Tab>Uploaders</Tab>
                <Tab>Payers</Tab>
              </TabList>
            </TabGroup>
          </div>
          <CohortMatrix
            cohorts={cohortsQuery.data?.cohorts ?? []}
            loading={cohortsQuery.isLoading}
            error={cohortsQuery.isError ? 'Falha ao carregar cohorts' : null}
            metric={cohortMetric}
            description="% de usuários da cohort com atividade na semana N."
          />
        </div>
      </div>
    </div>
  );
}

interface TableProps {
  items: UserListItem[];
  page: number;
  total: number;
  perPage: number;
  onPageChange: (n: number) => void;
}

function UsersTable({ items, page, total, perPage, onPageChange }: TableProps) {
  if (items.length === 0) {
    return (
      <div className="rounded border border-dashed border-slate-700 bg-slate-900/30 p-6 text-center text-sm text-slate-400">
        Nenhum usuário encontrado para os filtros atuais.
      </div>
    );
  }
  const lastPage = Math.max(1, Math.ceil(total / perPage));
  return (
    <div>
      <div className="overflow-x-auto rounded-lg border border-slate-800">
        <table className="min-w-full text-left text-sm text-slate-200">
          <thead className="bg-slate-900/60 text-xs uppercase text-slate-400">
            <tr>
              <th className="px-3 py-2 font-medium">User</th>
              <th className="px-3 py-2 font-medium">Org</th>
              <th className="px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2 font-medium text-right">Uploads</th>
              <th className="px-3 py-2 font-medium text-right">Calcs</th>
              <th className="px-3 py-2 font-medium text-right">Baixou</th>
              <th className="px-3 py-2 font-medium text-right">Pago</th>
              <th className="px-3 py-2 font-medium">Signup</th>
              <th className="px-3 py-2 font-medium">Last seen</th>
            </tr>
          </thead>
          <tbody>
            {items.map((u) => (
              <tr
                key={u.id}
                className="border-t border-slate-800 hover:bg-slate-900/40"
              >
                <td className="px-3 py-2">
                  <Link
                    to={`/users/${u.id}` as never}
                    className="block leading-tight text-blue-300 hover:underline"
                  >
                    {u.name ?? u.email}
                    <div className="text-xs text-slate-500">{u.email}</div>
                  </Link>
                </td>
                <td className="px-3 py-2 text-slate-400">{u.org_name ?? '—'}</td>
                <td className="px-3 py-2">
                  <StatusBadge status={u.status} />
                </td>
                <td className="px-3 py-2 text-right">{u.uploads_count}</td>
                <td className="px-3 py-2 text-right">{u.calc_completed_count}</td>
                <td className="px-3 py-2 text-right">
                  {u.downloaded_count > 0 ? (
                    <span className="text-emerald-400">{u.downloaded_count}</span>
                  ) : u.calc_completed_count > 0 ? (
                    <span className="text-amber-400" title="gerou cálculo mas não baixou">
                      0
                    </span>
                  ) : (
                    <span className="text-slate-500">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right">{u.total_paid_brl}</td>
                <td className="px-3 py-2 text-slate-400">{fmtDate(u.signup_at)}</td>
                <td className="px-3 py-2 text-slate-400">
                  {fmtDate(u.last_active_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex items-center justify-between text-xs text-slate-400">
        <span>
          Página {page} de {lastPage} — total {total}
        </span>
        <div className="space-x-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => onPageChange(Math.max(1, page - 1))}
            className="rounded border border-slate-700 px-2 py-1 disabled:opacity-40"
          >
            ←
          </button>
          <button
            type="button"
            disabled={page >= lastPage}
            onClick={() => onPageChange(Math.min(lastPage, page + 1))}
            className="rounded border border-slate-700 px-2 py-1 disabled:opacity-40"
          >
            →
          </button>
        </div>
      </div>
    </div>
  );
}
